/**
 * Helpers for the `_optimistic` payload the cart store draws a row from before
 * the add request comes back (see `vue/store/cart.js`).
 *
 * Note what is *not* here: a money parser. This theme's serialisers emit prices
 * as cents — quoted, but cents (`helper-best-sellers.liquid`) — so a caller
 * passes `Number(variant.price)` and nothing needs converting. Anything that
 * has been through Liquid's `money` filter is a display string and cannot be
 * used for a row.
 */

/**
 * `nil | img_url` hands back the admin's "no image" placeholder, a non-empty
 * string that beats every fallback testing it for truthiness — which is how a
 * row ended up showing that grey square for a variant with no image of its own.
 */
export const hasImage = (value) =>
  typeof value === "string" && value !== "" && !/no-image/.test(value);

/**
 * The image a row should show: the variant's own when it has one, otherwise the
 * product's — the same order the cards use.
 */
export const imageOf = (product, variant) =>
  [
    variant?.featured_image?.src,
    variant?.featured_image,
    product?.featured_image?.src,
    product?.featured_image,
    product?.images?.[0]?.src,
    product?.images?.[0],
  ].find(hasImage) || "";

/**
 * The plan list in the shape the row reads it — `product.selling_plan_groups`
 * — from the `sellingPlans` array the theme's serialisers emit. The row's
 * "Update to subscribe" button and its plan select are both gated on this, and
 * it is otherwise only known once the product JSON has been fetched for the
 * line, seconds after the row appeared.
 */
export const planGroupsFrom = (sellingPlans) => {
  if (!sellingPlans?.length) return undefined;

  return [
    {
      selling_plans: sellingPlans.map((plan) => ({
        id: plan.planId ?? plan.id,
        name: plan.name,
        recurring_deliveries: true,
      })),
    },
  ];
};

/**
 * The row's fields for a variant of a product, as Shopify's own product JSON
 * describes it — which is what the theme's sections already carry (`product |
 * json` in `dynamic-product.liquid`), so nothing here is recomputed from what
 * the page happens to display.
 *
 * @param product Shopify product JSON (prices in cents)
 * @param variantId the variant being added
 * @param planId selling plan id when subscribing: its price is what the row
 *   should show, not the one-time price
 */
export const optimisticFromProduct = (product, variantId, planId = null) => {
  const variant = product?.variants?.find((v) => v.id == variantId);
  if (!product || !variant) return undefined;

  const allocation = planId
    ? variant.selling_plan_allocations?.find((a) => a.selling_plan_id == planId)
    : null;

  const variantTitle =
    variant.title === "Default Title" ? "" : variant.title || "";

  return {
    product_title: product.title,
    variant_title: variantTitle,
    product_id: product.id,
    price: allocation?.price ?? variant.price,
    compare_at_price:
      allocation?.compare_at_price ??
      variant.compare_at_price ??
      product.compare_at_price ??
      null,
    image: imageOf(product, variant),
    handle: product.handle,
    sku: variant.sku,
    available: variant.available,
    selling_plan_name: planId
      ? allocation?.selling_plan?.name ||
        product.selling_plan_groups
          ?.flatMap((g) => g.selling_plans || [])
          .find((p) => p.id == planId)?.name
      : undefined,
    selling_plan_groups: product.selling_plan_groups,
  };
};

/**
 * The row's fields as `helper-optimistic-attrs.liquid` puts them on a button,
 * for the theme's card and submit buttons, which are handed a variant id and
 * nothing else. Returns nothing when the attributes are absent — the add then
 * waits for the response, the way it used to, rather than drawing a blank row.
 */
const parsePlans = (value) => {
  if (!value) return undefined;
  try {
    return planGroupsFrom(JSON.parse(value));
  } catch (error) {
    console.error("optimistic line: bad data-line-plans", error);
    return undefined;
  }
};

export const optimisticFromDataset = (dataset) => {
  if (!dataset || !dataset.lineProductTitle) return undefined;

  const variantTitle =
    dataset.lineVariantTitle === "Default Title"
      ? ""
      : dataset.lineVariantTitle || "";

  return {
    product_title: dataset.lineProductTitle,
    variant_title: variantTitle,
    product_id: Number(dataset.lineProductId) || null,
    // Cents, straight from Liquid.
    price: Number(dataset.linePrice),
    compare_at_price: Number(dataset.lineCompare) || null,
    image: hasImage(dataset.lineImage) ? dataset.lineImage : "",
    handle: dataset.lineHandle,
    sku: dataset.lineSku,
    available: true,
    selling_plan_groups: parsePlans(dataset.linePlans),
  };
};
